"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ColorGridWidget = exports.ColorGridPreset = exports.ColorSwatchWidget = void 0;
var tslib_1 = require("tslib");
var React = tslib_1.__importStar(require("react"));
var TypeStyle = tslib_1.__importStar(require("typestyle"));
var FocusIndicators_1 = require("../../styles/FocusIndicators");
var TabStyles_1 = require("../../styles/TabStyles");
var Declarative_1 = require("../../utils/Declarative");
var ReactHelpers_1 = require("../../utils/ReactHelpers");
var ColorPickerPresets_1 = require("./ColorPickerPresets");
Object.defineProperty(exports, "ColorGridPreset", { enumerable: true, get: function () { return ColorPickerPresets_1.ColorGridPreset; } });
var ColorSwatchSize = 15;
var ColorSwatchDivider = function () { return React.createElement("div", { style: { width: ColorSwatchSize, height: 4 } }); };
var ColorSwatchStyleName = TypeStyle.style({
    boxSizing: 'border-box',
    height: ColorSwatchSize,
    width: ColorSwatchSize,
    $nest: {
        '&:hover, &:active, &[aria-selected="true"]': { position: 'relative' },
        '&:focus-visible': tslib_1.__assign({ position: 'relative' }, FocusIndicators_1.DefaultFocusIndicator({ outlineOffset: -1, inset: true })),
        '&[aria-selected="true"]::after': { outline: "1px solid " + TabStyles_1.TabStyles.Colors.L8 },
        '&:hover::after, &:active::after': { outline: "1px solid " + TabStyles_1.TabStyles.Colors.L9 },
        '&:hover::after, &:active::after, &[aria-selected="true"]::after': {
            border: "1px solid white",
            bottom: -1,
            content: "\"\"",
            left: -1,
            pointerEvents: 'none',
            position: 'absolute',
            right: -1,
            top: -1,
        },
    }
});
var DataColorAttr = 'data-color';
exports.ColorSwatchWidget = React.memo(React.forwardRef(function (props, ref) {
    var _a;
    var propsColor = props.color, className = props.className, disabled = props.disabled, onClick = props.onClick, selected = props.selected, size = props.size, style = props.style, testId = props.testId, htmlAttrs = tslib_1.__rest(props, ["color", "className", "disabled", "onClick", "selected", "size", "style", "testId"]);
    var color = propsColor.toLowerCase();
    var handleClick = React.useCallback(function () { return onClick(color); }, [onClick, color]);
    return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    React.createElement("div", tslib_1.__assign({}, ReactHelpers_1.EventUtilsOptOut, { "aria-disabled": disabled, "aria-label": color, "aria-selected": selected, "data-tb-test-id": Declarative_1.makeTestId(testId, 'ColorSwatch', color) }, htmlAttrs, { className: TypeStyle.classes(className, ColorSwatchStyleName, disabled && 'disabled') }, (_a = {}, _a[DataColorAttr] = color, _a), { onClick: disabled ? undefined : handleClick, ref: ref, style: tslib_1.__assign({ backgroundColor: color, height: size, width: size }, style) })));
}));
exports.ColorSwatchWidget.displayName = 'ColorSwatchWidget';
var ColorGridStyleName = TypeStyle.style({ display: 'flex', gap: 3 });
var ColorGridWidget = /** @class */ (function (_super) {
    tslib_1.__extends(ColorGridWidget, _super);
    function ColorGridWidget() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.handleKeyDown = ReactHelpers_1.mergeEventHandlers(function (e) {
            var cellElem = e.target;
            var gridElem = e.currentTarget;
            var color = cellElem.getAttribute(DataColorAttr);
            if (!color) {
                return;
            }
            var rowIndex = Number(cellElem.getAttribute('aria-rowindex')) - 1;
            var colIndex = Number(cellElem.getAttribute('aria-colindex')) - 1;
            var focusCell = function (row, col) { var _a; return (_a = e.currentTarget.querySelector("[aria-rowindex=\"" + (row + 1) + "\"][aria-colindex=\"" + (col + 1) + "\"]")) === null || _a === void 0 ? void 0 : _a.focus(); };
            var colCount = Number(gridElem.getAttribute('aria-colcount'));
            var rowCount = Number(gridElem.getAttribute('aria-rowcount'));
            // we don't do any bounds checking on the indexes in here because the focus query will simply fail if its out of bounds
            var handlers = {
                'Enter': function () { return _this.props.handleSwatchClicked(color); },
                ' ': function () { return handlers.Enter(); },
                'ArrowLeft': function () { return focusCell(rowIndex, colIndex - 1); },
                'ArrowRight': function () { return focusCell(rowIndex, colIndex + 1); },
                'ArrowDown': function () { return focusCell(rowIndex + 1, colIndex); },
                'ArrowUp': function () { return focusCell(rowIndex - 1, colIndex); },
                'Ctrl+Home': function () { return focusCell(0, 0); },
                'Home': function () { return focusCell(rowIndex, 0); },
                'End': function () { return focusCell(rowIndex, colCount - 1); },
                'Ctrl+End': function () { return focusCell(rowCount - 1, colCount - 1); },
            };
            var key = "" + (e.ctrlKey ? 'Ctrl+' : '') + e.key;
            if (handlers[key]) {
                handlers[key]();
                e.preventDefault();
                e.stopPropagation();
            }
        }, function (e) { var _a, _b; return (_b = (_a = _this.props).onKeyDown) === null || _b === void 0 ? void 0 : _b.call(_a, e); });
        return _this;
    }
    ColorGridWidget.prototype.render = function () {
        var _a = this.props, className = _a.className, colorsPreset = _a.colorsPreset, disabled = _a.disabled, handleSwatchClicked = _a.handleSwatchClicked, selectedColor = _a.selectedColor, testId = _a.testId, htmlAttrs = tslib_1.__rest(_a, ["className", "colorsPreset", "disabled", "handleSwatchClicked", "selectedColor", "testId"]);
        var _b = this.buildColorSwatchGrid(), rowCount = _b.rowCount, colCount = _b.colCount, gridItems = _b.gridItems;
        // eslint-disable-next-line jsx-a11y/interactive-supports-focus
        return React.createElement("div", tslib_1.__assign({ "data-tb-test-id": Declarative_1.makeTestId(this.props.testId, 'ColorGrid') }, htmlAttrs, { "aria-colcount": colCount, "aria-rowcount": rowCount, className: TypeStyle.classes(className, ColorGridStyleName), children: gridItems, onKeyDown: this.handleKeyDown, role: 'grid' }));
    };
    ColorGridWidget.prototype.buildColorSwatchGrid = function () {
        var _a = this.props, colorsPreset = _a.colorsPreset, selectedColor = _a.selectedColor;
        var selectedColorIsInPreset = !!selectedColor && ColorPickerPresets_1.isColorInPreset(selectedColor, colorsPreset);
        var colors = ColorPickerPresets_1.ColorPickerPresets[colorsPreset];
        var colCount = colors.length;
        var rowCount = colors[0].filter(function (c) { return c !== '|'; }).length; // aria-rowcount and aria-rowindex should not include dividers
        var columns = [];
        var _loop_1 = function (colIdx) {
            var currentColumn = [];
            var seenDivider = false;
            var currentGroup = [];
            var closeGroup = function () {
                currentColumn.push(React.createElement("div", { children: currentGroup, style: { border: "1px solid " + TabStyles_1.TabStyles.Colors.L6 }, key: "group-" + (seenDivider ? 1 : 0) }));
                currentGroup = [];
            };
            for (var rowIdx = 0; rowIdx < colors[colIdx].length; rowIdx++) {
                var color = colors[colIdx][rowIdx].toLowerCase();
                if (color === '|') {
                    closeGroup();
                    currentColumn.push(React.createElement(ColorSwatchDivider, { key: 'divider' }));
                    seenDivider = true;
                    continue;
                }
                var selected = selectedColor === color;
                currentGroup.push(React.createElement(exports.ColorSwatchWidget, { "aria-colindex": colIdx + 1, "aria-rowindex": rowIdx + (seenDivider ? 0 : 1), color: color, disabled: this_1.props.disabled, key: 'swatch-' + color, onClick: this_1.props.handleSwatchClicked, role: 'gridcell', selected: selected, size: ColorSwatchSize, testId: this_1.props.testId, tabIndex: selected || (colIdx === 0 && rowIdx === 0 && !selectedColorIsInPreset) ? 0 : -1 }));
            }
            closeGroup();
            columns.push(React.createElement("div", { children: currentColumn, key: "column-" + colIdx }));
        };
        var this_1 = this;
        for (var colIdx = 0; colIdx < colCount; colIdx++) {
            _loop_1(colIdx);
        }
        return { rowCount: rowCount, colCount: colCount, gridItems: columns };
    };
    ColorGridWidget.defaultProps = { colorsPreset: ColorPickerPresets_1.ColorGridPreset.Dark };
    return ColorGridWidget;
}(React.PureComponent));
exports.ColorGridWidget = ColorGridWidget;
//# sourceMappingURL=ColorGridWidget.js.map